import { useParams, Link } from "react-router-dom";
import { useState, useEffect, useCallback, useRef } from "react";
import { useLang } from "@/i18n/LangContext";
import { API_BASE, api } from "@/lib/api";
import { getLocaleString } from "@/lib/format";
import { sanitizeHtml } from "@/lib/bbcode";
import { toSteamLang } from "@/lib/steam-locale";
import Spinner from "@/components/Spinner";
import AdBanner from "@/components/AdBanner";
import ReviewsSection from "./game-detail/ReviewsSection";
import SystemRequirements from "./game-detail/SystemRequirements";
import type { EpicGame, AppDetail, Review, ReviewSummary } from "@/types/api";

/* ─── Helpers ─── */

function isValidImageUrl(url: string) {
  return url && (url.startsWith("http://") || url.startsWith("https://"));
}

function getHeroImage(game: EpicGame) {
  const find = (type: string) =>
    game.keyImages.find((img) => img.type === type && isValidImageUrl(img.url))?.url || "";
  return (
    find("DieselStoreFrontWide") ||
    find("OfferImageWide") ||
    find("DieselGameBoxWide") ||
    game.keyImages.find((img) => isValidImageUrl(img.url))?.url ||
    ""
  );
}

function getThumbnail(game: EpicGame) {
  const find = (type: string) =>
    game.keyImages.find((img) => img.type === type && isValidImageUrl(img.url))?.url || "";
  return (
    find("Thumbnail") ||
    find("OfferImageTall") ||
    find("DieselGameBoxTall") ||
    game.keyImages.find((img) => isValidImageUrl(img.url))?.url ||
    ""
  );
}

function getRemainingDays(endDate?: string | null): number | null {
  if (!endDate) return null;
  const end = new Date(endDate).getTime();
  const diff = end - Date.now();
  if (diff <= 0) return 0;
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

function normalize(s: string) {
  return s.toLowerCase().replace(/[^a-z0-9\u3131-\uD79D\u4E00-\u9FFF]/g, "");
}

/* ─── Countdown Component ─── */

function Countdown({ endDate }: { endDate: string }) {
  const { t } = useLang();
  const [remaining, setRemaining] = useState("");

  useEffect(() => {
    const update = () => {
      const end = new Date(endDate).getTime();
      const diff = end - Date.now();
      if (diff <= 0) {
        setRemaining(t.freeGames.ended);
        return;
      }
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      if (days > 0) {
        setRemaining(
          `${days}${t.gameDetail.dayUnit} ${hours}${t.gameDetail.hourUnit} ${minutes}${t.gameDetail.minuteUnit}`
        );
      } else {
        setRemaining(
          `${hours}${t.gameDetail.hourUnit} ${minutes}${t.gameDetail.minuteUnit} ${seconds.toString().padStart(2, "0")}${t.gameDetail.secondUnit}`
        );
      }
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [endDate, t]);

  const days = getRemainingDays(endDate);
  const isUrgent = days !== null && days <= 2;

  return (
    <div
      className={`flex items-center justify-center gap-3 w-full px-6 py-4 rounded-2xl text-base font-bold shadow-md ${
        isUrgent
          ? "bg-rose-600 text-white shadow-rose-600/25 animate-pulse"
          : "bg-emerald-500 text-white shadow-emerald-500/25"
      }`}
    >
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
        <circle cx="12" cy="12" r="10" />
        <polyline points="12 6 12 12 16 14" />
      </svg>
      <span>
        {t.freeGames.freeEndsIn} {remaining}
      </span>
    </div>
  );
}

/* ─── Main Page ─── */

export default function FreeGameDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { lang, t } = useLang();
  const locale = getLocaleString(lang);

  // Epic game data
  const [game, setGame] = useState<EpicGame | null>(null);
  const [status, setStatus] = useState<"free" | "upcoming">("free");
  const [pageLoading, setPageLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  // Steam matching
  const [steamApp, setSteamApp] = useState<AppDetail | null>(null);
  const [steamAppid, setSteamAppid] = useState<string | null>(null);
  const [steamLoading, setSteamLoading] = useState(false);
  const [steamNotFound, setSteamNotFound] = useState(false);
  const [hasSteamData, setHasSteamData] = useState(false);
  const [playerCount, setPlayerCount] = useState<number | null>(null);

  // Reviews
  const [reviewSummary, setReviewSummary] = useState<ReviewSummary | null>(null);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [reviewLang, setReviewLang] = useState<string>(toSteamLang(lang));
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [reviewCursor, setReviewCursor] = useState("*");
  const [reviewsHasMore, setReviewsHasMore] = useState(true);

  // Image modal
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  // Step 1: Fetch Epic free games and find the matching game by id
  useEffect(() => {
    if (!id) return;
    const fetchGame = async () => {
      try {
        const res = await fetch(`${API_BASE}/free-games?lang=${lang}`);
        if (!res.ok) {
          setNotFound(true);
          setPageLoading(false);
          return;
        }
        const data = await res.json();
        const epicFreeNow: EpicGame[] = data.epic?.freeNow || data.epic?.current || [];
        const epicUpcoming: EpicGame[] = data.epic?.upcoming || [];

        let found = epicFreeNow.find((g) => g.id === id);
        let foundStatus: "free" | "upcoming" = "free";
        if (!found) {
          found = epicUpcoming.find((g) => g.id === id);
          foundStatus = "upcoming";
        }
        if (!found) {
          setNotFound(true);
          setPageLoading(false);
          return;
        }
        setGame(found);
        setStatus(foundStatus);
        setPageLoading(false);
      } catch {
        setNotFound(true);
        setPageLoading(false);
      }
    };
    fetchGame();
  }, [id, lang]);

  // Step 2: Search Steam for matching game
  useEffect(() => {
    if (!game) return;
    const searchSteam = async () => {
      setSteamLoading(true);
      try {
        const res = await fetch(api.store.gameSearch(game.title, lang));
        if (!res.ok) {
          setSteamNotFound(true);
          setSteamLoading(false);
          return;
        }
        const data = await res.json();
        const items = data.items || [];
        if (items.length === 0) {
          setSteamNotFound(true);
          setSteamLoading(false);
          return;
        }

        const n2 = normalize(game.title);
        const exact = items.find((item: { name: string }) => normalize(item.name) === n2);
        if (exact) {
          setSteamAppid(String(exact.appid));
          return;
        }

        const first = items[0];
        const n1 = normalize(first.name);
        if (n1.includes(n2) || n2.includes(n1)) {
          setSteamAppid(String(first.appid));
          return;
        }

        setSteamNotFound(true);
        setSteamLoading(false);
      } catch {
        setSteamNotFound(true);
        setSteamLoading(false);
      }
    };
    searchSteam();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game?.title, lang]);

  // Step 3: Fetch Steam details + players
  useEffect(() => {
    if (!steamAppid) return;
    const fetchSteamData = async () => {
      const [detailRes, playersRes] = await Promise.allSettled([
        fetch(api.store.appDetails(steamAppid, lang)),
        fetch(api.store.players(steamAppid)),
      ]);

      if (detailRes.status === "fulfilled" && detailRes.value.ok) {
        const data = await detailRes.value.json();
        const appData = data[steamAppid];
        if (appData?.success) {
          setSteamApp(appData.data);
          setHasSteamData(true);
        }
      }
      if (playersRes.status === "fulfilled" && playersRes.value.ok) {
        const data = await playersRes.value.json();
        if (data.response?.player_count !== undefined) setPlayerCount(data.response.player_count);
      }
      setSteamLoading(false);
    };
    fetchSteamData();
  }, [steamAppid, lang]);

  // Step 4: Fetch reviews
  useEffect(() => {
    if (!steamAppid) return;
    const fetchReviews = async () => {
      setReviewsLoading(true);
      setReviews([]);
      setReviewCursor("*");
      setReviewsHasMore(true);
      try {
        const [listRes, summaryRes] = await Promise.all([
          fetch(
            api.store.reviews({
              appid: steamAppid,
              filter: "recent",
              num_per_page: 20,
              language: reviewLang,
              lang,
            })
          ),
          fetch(
            api.store.reviews({
              appid: steamAppid,
              filter: "all",
              num_per_page: 0,
              language: reviewLang,
              lang,
            })
          ),
        ]);
        if (listRes.ok) {
          const listData = await listRes.json();
          const summaryData = summaryRes.ok ? await summaryRes.json() : null;
          setReviewSummary(summaryData?.query_summary ?? listData.query_summary);
          if (listData.reviews) setReviews(listData.reviews);
          if (listData.cursor) setReviewCursor(listData.cursor);
          setReviewsHasMore((listData.reviews?.length ?? 0) >= 20);
        }
      } catch {
        /* ignore */
      }
      setReviewsLoading(false);
    };
    fetchReviews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [steamAppid, reviewLang, lang]);

  // Load more reviews (infinite scroll)
  const reviewCursorRef = useRef(reviewCursor);
  reviewCursorRef.current = reviewCursor;
  const loadMoreReviews = useCallback(async () => {
    if (!steamAppid || reviewsLoading || !reviewsHasMore) return;
    setReviewsLoading(true);
    try {
      const res = await fetch(
        api.store.reviews({
          appid: steamAppid,
          filter: "recent",
          num_per_page: 20,
          cursor: reviewCursorRef.current,
          language: reviewLang,
          lang,
        })
      );
      if (!res.ok) {
        setReviewsHasMore(false);
        setReviewsLoading(false);
        return;
      }
      const data = await res.json();
      if (data.reviews?.length > 0) {
        setReviews((prev) => [...prev, ...data.reviews]);
        if (data.cursor) setReviewCursor(data.cursor);
        setReviewsHasMore(!!data.cursor && data.cursor !== "*");
      } else {
        setReviewsHasMore(false);
      }
    } catch {
      setReviewsHasMore(false);
    }
    setReviewsLoading(false);
  }, [steamAppid, reviewsLoading, reviewsHasMore, reviewLang, lang]);

  // IntersectionObserver sentinel for infinite scroll
  const observerRef = useRef<IntersectionObserver | null>(null);
  const reviewsSentinelCallback = useCallback(
    (node: HTMLDivElement | null) => {
      if (observerRef.current) observerRef.current.disconnect();
      if (!node) return;
      observerRef.current = new IntersectionObserver(
        (entries) => {
          if (entries[0]?.isIntersecting) loadMoreReviews();
        },
        { rootMargin: "400px" }
      );
      observerRef.current.observe(node);
    },
    [loadMoreReviews]
  );

  /* ─── Loading / Not Found ─── */

  if (pageLoading) return <Spinner />;
  if (notFound || !game) {
    return (
      <div className="max-w-[1320px] mx-auto px-4 sm:px-6 py-12 text-center">
        <p className="text-sm text-gray-400">{t.freeGames.notFound}</p>
        <Link to="/free" className="text-sm text-blue-500 hover:text-blue-600 font-medium mt-3 inline-block">
          {t.freeGames.backToListShort}
        </Link>
      </div>
    );
  }

  /* ─── Derived values ─── */

  const heroImage = getHeroImage(game);
  const thumbnailImage = getThumbnail(game);
  const sellerName = game.seller?.name || "";
  const releaseDate = game.effectiveDate
    ? new Date(game.effectiveDate).toLocaleDateString(locale, {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : null;
  const allImages = game.keyImages
    .filter((img) => isValidImageUrl(img.url) && img.type !== "Thumbnail")
    .map((img) => img.url);

  return (
    <div className="max-w-[1320px] mx-auto px-4 sm:px-6 py-6">
      {/* Back link */}
      <Link
        to="/free"
        className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-400 hover:text-gray-600 transition-colors mb-4"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M19 12H5M12 19l-7-7 7-7" />
        </svg>
        {t.freeGames.backToList}
      </Link>

      {/* Hero image */}
      {heroImage && (
        <div className="relative w-full aspect-[96/40] rounded-2xl overflow-hidden mb-6 bg-gray-100">
          <img src={heroImage} alt={game.title} className="w-full h-full object-cover" />
        </div>
      )}

      {/* Countdown */}
      {status === "free" && game.promoEndDate && (
        <div className="mb-6">
          <Countdown endDate={game.promoEndDate} />
        </div>
      )}

      {/* Title + price card */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4 sm:p-6 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 sm:gap-6">
          <div className="flex-1 min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3">{game.title}</h1>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-500 mb-4">
              {sellerName && (
                <span>
                  {t.freeGames.devPublisher}{" "}
                  <span className="text-gray-700">{sellerName}</span>
                </span>
              )}
              {releaseDate && (
                <>
                  <span className="text-gray-300">|</span>
                  <span>
                    {t.freeGames.releaseLabel}{" "}
                    <span className="text-gray-700">{releaseDate}</span>
                  </span>
                </>
              )}
            </div>
            {status === "free" && (
              <span className="inline-block text-xs font-bold bg-emerald-500 text-white px-2.5 py-1 rounded-lg">
                {t.freeGames.freeNow}
              </span>
            )}
            {status === "upcoming" && (
              <span className="inline-block text-xs font-bold bg-blue-500 text-white px-2.5 py-1 rounded-lg">
                {t.freeGames.freeScheduled}
              </span>
            )}
          </div>
          <div className="shrink-0 flex flex-row sm:flex-col items-center sm:items-end gap-3">
            <span className="text-lg font-bold text-emerald-600 bg-emerald-50 px-4 py-2 rounded-xl border border-emerald-200">
              {t.common.free}
            </span>
            <span className="text-[11px] text-gray-400 bg-gray-50 px-2.5 py-1 rounded-md border border-gray-200 font-medium">
              {t.freeGames.epicGames}
            </span>
          </div>
        </div>
        {/* End date info (no external link) */}
        {game.promoEndDate && (
          <div className="mt-5 pt-5 border-t border-gray-100">
            <span className="text-sm text-gray-400">
              {t.freeGames.endsLabel}{" "}
              {new Date(game.promoEndDate).toLocaleDateString(locale, {
                year: "numeric",
                month: "long",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })}
            </span>
          </div>
        )}
      </div>

      {/* ─── Steam Data Section ─── */}

      {steamLoading && !steamNotFound && (
        <div className="flex items-center justify-center gap-3 py-12">
          <div className="w-6 h-6 border-[3px] border-gray-200 border-t-blue-500 rounded-full animate-spin" />
          <span className="text-sm text-gray-500">{t.freeGames.steamSearching}</span>
        </div>
      )}

      {steamApp && steamAppid && (
        <>
          {/* Also on Steam banner → internal link */}
          <Link
            to={`/game/${steamAppid}`}
            className="flex items-center justify-between bg-gradient-to-r from-[#1b2838] to-[#2a475e] rounded-2xl px-5 py-4 mb-6 group hover:shadow-lg transition-shadow"
          >
            <div className="flex items-center gap-3">
              <img
                src={steamApp.capsule_image}
                alt={steamApp.name}
                className="w-16 h-8 rounded object-cover hidden sm:block"
                loading="lazy"
              />
              <div>
                <p className="text-sm font-bold text-white">{t.freeGames.alsoOnSteam}</p>
                <p className="text-xs text-gray-300">{t.freeGames.steamInfoDesc}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {playerCount !== null && (
                <span className="text-xs text-emerald-400 font-medium hidden sm:block">
                  {t.freeGames.currentPlayers}: {playerCount.toLocaleString(locale)}
                </span>
              )}
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="white"
                strokeWidth="2"
                className="group-hover:translate-x-1 transition-transform"
              >
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </div>
          </Link>

          {/* Game Info Table */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm mb-6 overflow-hidden">
            <div className="px-6 pt-5 pb-3">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-gray-900 flex items-center justify-center">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <path d="M12 16v-4M12 8h.01" />
                  </svg>
                </div>
                <h3 className="text-lg font-bold text-gray-900">{t.gameDetail.gameInfo}</h3>
              </div>
            </div>
            <div className="divide-y divide-gray-100">
              {steamApp.developers?.length > 0 && (
                <div className="flex items-center px-6 py-3">
                  <span className="w-32 shrink-0 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                    {t.gameDetail.developerLabel}
                  </span>
                  <span className="text-sm text-gray-900 font-medium">
                    {steamApp.developers.join(", ")}
                  </span>
                </div>
              )}
              {steamApp.publishers?.length > 0 && (
                <div className="flex items-center px-6 py-3">
                  <span className="w-32 shrink-0 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                    {t.gameDetail.publisherLabel}
                  </span>
                  <span className="text-sm text-gray-900 font-medium">
                    {steamApp.publishers.join(", ")}
                  </span>
                </div>
              )}
              {steamApp.release_date && (
                <div className="flex items-center px-6 py-3">
                  <span className="w-32 shrink-0 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                    {t.gameDetail.releaseDateLabel}
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-900 font-medium">{steamApp.release_date.date}</span>
                    {steamApp.release_date.coming_soon && (
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-blue-50 text-blue-600">
                        {t.gameDetail.comingSoon}
                      </span>
                    )}
                  </div>
                </div>
              )}
              {steamApp.platforms && (
                <div className="flex items-center px-6 py-3">
                  <span className="w-32 shrink-0 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                    {t.gameDetail.platformLabel}
                  </span>
                  <div className="flex items-center gap-3">
                    {steamApp.platforms.windows && (
                      <span className="flex items-center gap-1 text-sm text-gray-900">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" className="text-gray-500">
                          <path d="M3 12.5l7-1V4L3 5.5v7zm0 7L10 21v-7.5l-7-1v7zm8-1L21 21V12h-10v6.5zm0-14v7.5H21V3L11 4.5z" />
                        </svg>
                        Windows
                      </span>
                    )}
                    {steamApp.platforms.mac && (
                      <span className="flex items-center gap-1 text-sm text-gray-900">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" className="text-gray-500">
                          <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
                        </svg>
                        macOS
                      </span>
                    )}
                    {steamApp.platforms.linux && (
                      <span className="flex items-center gap-1 text-sm text-gray-900">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" className="text-gray-500">
                          <path d="M12.504 0c-.155 0-.311.015-.466.045C9.49.401 7.803 2.926 7.803 5.594c0 .724.147 1.44.436 2.119.294.687.707 1.293 1.228 1.8.112.108.224.215.337.321.113.108.227.213.34.317v3.854H7.803v1.5h2.341v3.854c-.113.104-.227.21-.34.317-.113.106-.225.213-.337.321-.521.507-.934 1.113-1.228 1.8-.289.679-.436 1.395-.436 2.119 0 2.668 1.687 5.193 4.235 5.549.155.03.311.045.466.045s.311-.015.466-.045c2.548-.356 4.235-2.881 4.235-5.549 0-.724-.147-1.44-.436-2.119-.294-.687-.707-1.293-1.228-1.8-.112-.108-.224-.215-.337-.321-.113-.107-.227-.213-.34-.317v-3.854h2.341v-1.5H14.14V10.2c.113-.104.227-.209.34-.317.113-.106.225-.213.337-.321.521-.507.934-1.113 1.228-1.8.289-.679.436-1.395.436-2.119 0-2.668-1.687-5.193-4.235-5.549A3.612 3.612 0 0012.504 0z" />
                        </svg>
                        Linux
                      </span>
                    )}
                  </div>
                </div>
              )}
              {steamApp.genres && steamApp.genres.length > 0 && (
                <div className="flex items-start px-6 py-3">
                  <span className="w-32 shrink-0 text-xs font-semibold text-gray-400 uppercase tracking-wider pt-0.5">
                    {t.gameDetail.genreLabel}
                  </span>
                  <div className="flex flex-wrap gap-1.5">
                    {steamApp.genres.map((g) => (
                      <span
                        key={g.id}
                        className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-lg font-medium"
                      >
                        {g.description}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {steamApp.supported_languages && (
                <div className="flex items-start px-6 py-3 min-w-0">
                  <span className="w-32 shrink-0 text-xs font-semibold text-gray-400 uppercase tracking-wider pt-0.5">
                    {t.gameDetail.languagesLabel}
                  </span>
                  <p
                    className="text-xs text-gray-700 leading-relaxed min-w-0 break-words"
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(steamApp.supported_languages) }}
                  />
                </div>
              )}
              {steamApp.metacritic && (
                <div className="flex items-center px-6 py-3">
                  <span className="w-32 shrink-0 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                    Metacritic
                  </span>
                  <span
                    className={`text-sm font-bold px-2 py-0.5 rounded ${
                      steamApp.metacritic.score >= 75
                        ? "bg-emerald-500 text-white"
                        : steamApp.metacritic.score >= 50
                          ? "bg-amber-400 text-gray-900"
                          : "bg-red-500 text-white"
                    }`}
                  >
                    {steamApp.metacritic.score}
                  </span>
                </div>
              )}
            </div>
          </div>


          {/* Screenshots */}
          {steamApp.screenshots && steamApp.screenshots.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-bold text-gray-900 mb-3">{t.gameDetail.screenshots}</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                {steamApp.screenshots.slice(0, 6).map((ss) => (
                  <button
                    key={ss.id}
                    onClick={() => setSelectedImage(ss.path_full)}
                    className="relative w-full aspect-video rounded-xl overflow-hidden bg-gray-100 hover:ring-2 hover:ring-blue-400 hover:ring-offset-2 transition-all"
                  >
                    <img
                      src={ss.path_thumbnail}
                      alt={`${steamApp.name} screenshot`}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      decoding="async"
                    />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Reviews */}
          {steamAppid && (reviewSummary || reviews.length > 0) && (
            <ReviewsSection
              appid={steamAppid}
              appidNum={Number(steamAppid)}
              reviews={reviews}
              reviewSummary={reviewSummary}
              reviewLang={reviewLang}
              setReviewLang={setReviewLang}
              reviewsLoading={reviewsLoading}
              reviewsHasMore={reviewsHasMore}
              reviewsSentinelCallback={reviewsSentinelCallback}
              onLoadMore={loadMoreReviews}
            />
          )}

          {/* System Requirements */}
          {steamApp.pc_requirements?.minimum && (
            <SystemRequirements
              minimum={steamApp.pc_requirements.minimum}
              recommended={steamApp.pc_requirements.recommended}
            />
          )}
        </>
      )}

      {/* Epic image gallery — only show if no Steam screenshots */}
      {!hasSteamData && allImages.length > 1 && (
        <div className="mb-6">
          <h2 className="text-lg font-bold text-gray-900 mb-3">{t.freeGames.image}</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
            {allImages.map((url, i) => (
              <button
                key={i}
                onClick={() => setSelectedImage(url)}
                className="relative w-full aspect-video rounded-xl overflow-hidden bg-gray-100 hover:ring-2 hover:ring-blue-400 hover:ring-offset-2 transition-all"
              >
                <img
                  src={url}
                  alt={`${game.title} screenshot ${i + 1}`}
                  className="w-full h-full object-cover"
                  loading="lazy"
                  decoding="async"
                />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Image modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <div className="relative max-w-5xl w-full aspect-video">
            <img
              src={selectedImage}
              alt={`${game.title} screenshot`}
              className="w-full h-full object-contain"
            />
          </div>
          <button
            onClick={() => setSelectedImage(null)}
            className="absolute top-4 right-4 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white text-xl transition-colors"
          >
            &times;
          </button>
        </div>
      )}

      {/* Description */}
      {game.description && (
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 mb-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4">{t.gameDetail.aboutGame}</h2>
          <p className="text-sm text-gray-700 leading-7">{game.description}</p>
        </div>
      )}

      {/* Promo info */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 mb-6">
        <div className="flex items-center gap-2.5 mb-4">
          <div className="w-8 h-8 rounded-lg bg-gray-900 flex items-center justify-center">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
          </div>
          <h2 className="text-lg font-bold text-gray-900">{t.freeGames.promoInfo}</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {game.promoStartDate && (
            <div className="bg-gray-50 rounded-xl border border-gray-200 p-4">
              <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1">
                {t.freeGames.freeStart}
              </p>
              <p className="text-sm font-bold text-gray-900">
                {new Date(game.promoStartDate).toLocaleDateString(locale, {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </p>
            </div>
          )}
          {game.promoEndDate && (
            <div className="bg-gray-50 rounded-xl border border-gray-200 p-4">
              <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1">
                {t.freeGames.freeEnd}
              </p>
              <p className="text-sm font-bold text-gray-900">
                {new Date(game.promoEndDate).toLocaleDateString(locale, {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </p>
            </div>
          )}
          <div className="bg-gray-50 rounded-xl border border-gray-200 p-4">
            <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1">
              {t.freeGames.status}
            </p>
            <p className="text-sm font-bold text-gray-900">
              {status === "free" ? t.freeGames.currentlyFree : t.freeGames.scheduledFree}
            </p>
          </div>
          <div className="bg-gray-50 rounded-xl border border-gray-200 p-4">
            <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1">
              {t.gameDetail.platformLabel}
            </p>
            <p className="text-sm font-bold text-gray-900">{t.freeGames.epicGames}</p>
          </div>
        </div>
      </div>

      {/* Cover thumbnail — only show if no Steam data */}
      {!hasSteamData && thumbnailImage && (
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 mb-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4">{t.freeGames.coverImage}</h2>
          <div className="flex justify-center">
            <div className="relative w-[300px] aspect-[3/4] rounded-xl overflow-hidden bg-gray-100">
              <img
                src={thumbnailImage}
                alt={game.title}
                className="w-full h-full object-cover"
                loading="lazy"
                decoding="async"
              />
            </div>
          </div>
        </div>
      )}

      <AdBanner className="my-4" />
    </div>
  );
}
